import { IUploadedAttachment } from '../../../types';
import { createZustandStore } from '../../../zustand';

import {
  IGradePopoverItem,
  IGradeRequestState,
  IGradeReviewReason,
  IGradeReviewStepItem,
  IInitializeableState,
  TGradeReviewStepId,
  TSetGradeRequestState,
} from './types';

/**
 * Updates the search input to the deal ID on the GRR search widget
 */
export function onUpdateSearchKeyCallback(set: TSetGradeRequestState) {
  return function onUpdateSearchKey(searchKey: string) {
    set(() => ({ searchKey }));
  };
}

/**
 * Updates the active step id in the GRR process.
 */
export function onUpdateCurrentStepIdCallback(set: TSetGradeRequestState) {
  return function onUpdateCurrentStepId(stepId: TGradeReviewStepId) {
    set(() => ({ currentStepId: stepId }));
  };
}

/**
 * Updates a specific step in memory for the GRR process.
 */
export function onUpdateStepCallback(set: TSetGradeRequestState) {
  return function onUpdateStep(
    stepId: TGradeReviewStepId,
    updates: Partial<IGradeReviewStepItem>
  ) {
    set(({ stepsMap }) => {
      const newMap = new Map(stepsMap);
      const existingStep = newMap.get(stepId);

      if (existingStep) {
        newMap.set(stepId, { ...existingStep, ...updates });
      }

      const steps = Array.from(newMap.values());

      return { stepsMap: newMap, steps };
    });
  };
}

/**
 * Updates a specific step in memory for the GRR process.
 */
export function onUpdateSelectedReviewReasonCallback(
  set: TSetGradeRequestState
) {
  return function onUpdateSelectedReviewReason(
    option: IGradeReviewReason | null
  ) {
    set(({ selectedReviewReasons }) => {
      if (!option) return { selectedReviewReasons: null };

      let newSelectedReviewReasons = null;

      if (selectedReviewReasons) {
        const foundItem = selectedReviewReasons.find(
          ({ feature }) => feature === option.feature
        );

        const isSameItem = foundItem?.id === option.id;

        if (foundItem) {
          newSelectedReviewReasons = selectedReviewReasons.filter(
            ({ feature }) => !(feature === option.feature)
          );

          if (!isSameItem) {
            newSelectedReviewReasons = [...newSelectedReviewReasons, option];
          }
        } else {
          newSelectedReviewReasons = [...selectedReviewReasons, option];
        }
      } else {
        newSelectedReviewReasons = [option];
      }

      return { selectedReviewReasons: newSelectedReviewReasons };
    });
  };
}

/**
 * Updates the proposed grade with what is sent from the user.
 */
export function onUpdateProposedGradeCallback(set: TSetGradeRequestState) {
  return function onUpdateProposedGrade(grade: IGradePopoverItem | null) {
    set(() => ({ proposedGrade: grade }));
  };
}

/**
 * Updates the comment entered by the user.
 */
export function onUpdateCommentCallback(set: TSetGradeRequestState) {
  return function onUpdateComment(comment: string | null) {
    set(() => ({ comment }));
  };
}

/**
 * Updates the images list uploaded by the user
 */
export function onUpdateImagesCallback(set: TSetGradeRequestState) {
  return function onUpdateImages(images: IUploadedAttachment[] | null) {
    set(() => ({ images }));
  };
}

/**
 * Updates the images list uploaded by the user
 */
export function onUpdateVideosCallback(set: TSetGradeRequestState) {
  return function onUpdateVideos(videos: IUploadedAttachment[] | null) {
    set(() => ({ videos }));
  };
}

/**
 * This helps generate the initial state for the
 */
const getInitialGRRState = (): Pick<
  IGradeRequestState,
  | 'steps'
  | 'stepsMap'
  | 'searchKey'
  | 'currentStepId'
  | 'selectedDealInfo'
  | 'gradeDefectIssues'
  | 'selectedGradeChangeRequest'
  | 'proposedGrade'
  | 'selectedReviewReasons'
  | 'comment'
  | 'images'
  | 'videos'
> => {
  const stepsMap = new Map<TGradeReviewStepId, IGradeReviewStepItem>();

  stepsMap.set('step-1', {
    id: 'step-1',
    description: 'step1.description',
    name: 'step1.name',
    isDone: false,
    title: 'step1.title',
  });

  stepsMap.set('step-2', {
    id: 'step-2',
    description: 'step2.description',
    name: 'step2.name',
    isDone: false,
    title: 'step2.title',
  });

  const steps = Array.from(stepsMap.values());

  return {
    steps,
    stepsMap,
    searchKey: '',
    currentStepId: 'step-1',
    selectedDealInfo: null,
    gradeDefectIssues: [],
    selectedGradeChangeRequest: null,
    proposedGrade: null,
    selectedReviewReasons: null,
    comment: null,
    images: null,
    videos: null,
  };
};

/**
 * Updates the images list uploaded by the user GradeChangeRequestStore
 */
export function onResetGradeChangeRequestStoreCallback(
  set: TSetGradeRequestState
) {
  return function onResetGradeChangeRequestStore() {
    const {
      steps,
      stepsMap,
      searchKey,
      currentStepId,
      selectedDealInfo,
      gradeDefectIssues,
      selectedGradeChangeRequest,
      proposedGrade,
      selectedReviewReasons,
      comment,
      images,
      videos,
    } = getInitialGRRState();

    set(() => ({
      steps,
      stepsMap,
      searchKey,
      currentStepId,
      selectedDealInfo,
      gradeDefectIssues,
      selectedGradeChangeRequest,
      proposedGrade,
      selectedReviewReasons,
      comment,
      images,
      videos,
    }));
  };
}

/**
 * This provides the initialization state for the the GRR form, allowing the UI
 * to prefill the relevant pieces of state.
 */
export function onInitializeGradeChangeRequestStoreCallback(
  set: TSetGradeRequestState
) {
  return function onInitializeGradeChangeRequestStore({
    comment,
    images,
    selectedDealInfo,
    selectedReviewReasons,
    videos,
  }: IInitializeableState) {
    set(() => ({
      selectedDealInfo,
      selectedReviewReasons,
      comment,
      images,
      videos,
    }));
  };
}

/**
 * This creates a local global state for the grade change request
 */
export const useGradeChangeRequestStore =
  createZustandStore<IGradeRequestState>()((set) => {
    const {
      steps,
      stepsMap,
      searchKey,
      currentStepId,
      selectedDealInfo,
      gradeDefectIssues,
      selectedGradeChangeRequest,
      proposedGrade,
      selectedReviewReasons,
      comment,
      images,
      videos,
    } = getInitialGRRState();

    return {
      searchKey,
      onUpdateSearchKey: onUpdateSearchKeyCallback(set),
      currentStepId,
      onUpdateCurrentStepId: onUpdateCurrentStepIdCallback(set),
      gradeDefectIssues,
      selectedDealInfo,
      selectedGradeChangeRequest,
      selectedReviewReasons,
      onUpdateSelectedReviewReason: onUpdateSelectedReviewReasonCallback(set),
      stepsMap,
      steps,
      onUpdateStep: onUpdateStepCallback(set),
      proposedGrade,
      onUpdateProposedGrade: onUpdateProposedGradeCallback(set),
      comment,
      onUpdateComment: onUpdateCommentCallback(set),
      images,
      onUpdateImages: onUpdateImagesCallback(set),
      videos,
      onUpdateVideos: onUpdateVideosCallback(set),
      updateSelectedGradeChangeRequest: (selectedGrade) => {
        set(() => ({ selectedGradeChangeRequest: selectedGrade }));
      },
      selectedGradeComponentReasons: [],
      updateSelectedGradeComponentReasons: (reasons) => {
        set(() => ({ selectedGradeComponentReasons: reasons }));
      },
      updateGradeDefectIssues: (reasons) => {
        set(() => ({ gradeDefectIssues: reasons }));
      },
      updateSelectedDealInfo: (dealInfo) => {
        set(() => ({ selectedDealInfo: dealInfo }));
      },
      onResetGradeChangeRequestStore:
        onResetGradeChangeRequestStoreCallback(set),
      onInitializeGradeChangeRequestStore:
        onInitializeGradeChangeRequestStoreCallback(set),
    };
  });
